import { Users, List, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function EligibilityAdjustment() {
  return (
    <section className="py-12 px-4 bg-blue-50">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-blue-700 mb-2">
            Eligibility for Adjustment of Status
          </h2>
          <p className="text-lg font-semibold text-gray-900">
            What are the requirements?
          </p>
        </div>

        {/* Intro text */}
        <p className="text-gray-700 mb-8 leading-relaxed">
          Every family-based green card applicant falls into one of two groups:{' '}
          <Link to="#" className="text-blue-600 hover:underline">immediate relatives</Link> or{' '}
          <Link to="#" className="text-blue-600 hover:underline">family preference categories</Link>. 
          The group you belong to determines how quickly your case can move forward.
        </p>

        {/* Two cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Immediate Relative Categories */}
          <div className="bg-white border-2 border-blue-500 rounded-lg p-8">
            <div className="flex justify-center mb-4">
              <Users className="w-16 h-16 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-blue-600 text-center mb-4">
              Immediate Relative Categories
            </h3>
            <p className="text-gray-700 text-center mb-6">
              Immediate relatives include the spouse, parents, and unmarried children under age 21(i) of U.S. citizens.
            </p>
            <div className="text-center">
              <button className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-2 rounded transition font-semibold">
                Learn More About<br />IMMEDIATE RELATIVES
              </button>
            </div>
          </div>

          {/* Family Preference Categories */}
          <div className="bg-white border-2 border-blue-500 rounded-lg p-8">
            <div className="flex justify-center mb-4">
              <List className="w-16 h-16 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-blue-600 text-center mb-4">
              Family Preference Categories
            </h3>
            <p className="text-gray-700 text-center mb-6">
              Family preference includes other qualifying family-based relationships for immigration.
            </p>
            <div className="text-center">
              <button className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-2 rounded transition font-semibold">
                Learn More About<br />FAMILY PREFERENCES
              </button>
            </div>
          </div>
        </div>

        {/* Additional info paragraph */}
        <p className="text-gray-700 mb-8 leading-relaxed">
          For adjustment of status, it matters which category you fall into. Immediate relatives of U.S. citizens always have a visa available — there's no annual cap. Family preference applicants are subject to yearly limits. That means they typically have to wait until their category is "current" in the Visa Bulletin.
        </p>

        {/* Requirements section */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">
            Requirements to Adjust Status Inside the U.S.
          </h3>
          <p className="text-gray-700 mb-6 leading-relaxed">
            Adjustment of status is only available to certain people already inside the U.S. meeting specific legal criteria. At a minimum, you must:
          </p>

          {/* Requirements list */}
          <div className="space-y-6">
            {/* Requirement 1 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" fill="currentColor" />
                </div>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">
                  Be physically present in the United States
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  You must be inside the United States when the adjustment of status application is filed (and will need to complete the process inside the U.S.).
                </p>
              </div>
            </div>

            {/* Requirement 2 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" fill="currentColor" />
                </div>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">
                  Have entered legally
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Generally, you need to be able to prove that you entered the U.S. with valid documentation and made face-to-face contact with a U.S. immigration officer, and that officer acknowledged your entry to the United States. If you entered with a valid visa, but that visa has since expired, you still had a lawful entry.
                </p>
              </div>
            </div>

            {/* Requirement 3 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" fill="currentColor" />
                </div>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">
                  Have an immigrant visa number
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  You need both an approved petition and an available visa. Immediate relatives of U.S. citizens always have one. But if you're in a family preference category, you'll need to wait until your turn comes up.{' '}
                  <Link to="#" className="text-blue-600 hover:underline">See how to read the Visa Bulletin →</Link>
                </p>
              </div>
            </div>

            {/* Requirement 4 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" fill="currentColor" />
                </div>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">
                  Be admissible to the United States
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Admissibility means that there are no legal bars that would prevent approval of your green card or entry into the U.S. There are several{' '}
                  <Link to="#" className="text-blue-600 hover:underline">grounds of inadmissibility</Link>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer paragraphs */}
        <div className="space-y-4 text-gray-700 leading-relaxed">
          <p>
            Only a very limited group of people can adjust status. That's why adjustment is generally only used by some immediate relatives, spouses that entered as K-1 fiancés, asylees, refugees, or those who arrived on an employer sponsored visa (e.g. H-1B) and the employer sponsored them for a green card.
          </p>
          <p>
            Not sure if you meet the requirements? citizenhup helps you confirm eligibility and all requirements before you purchase our service.{' '}
            <Link to="#" className="text-blue-600 hover:underline">Get started on an Adjustment of Status Package for free →</Link>
          </p>
        </div>
      </div>
    </section>
  );
}